import { computed, inject, Injectable, Signal } from '@angular/core';
import { CreateMasterWageRateVars } from '../interfaces/create-master-wage-rate-input-vars.interface';
import { MasterWageRateFormService } from './master-wage-rate-form.service';
import { UpdateMasterWageRateVars } from '../interfaces/update-master-wage-rate-input.vars.interface';
import { LCPUserService } from 'src/app/shared/services/lcp-user/lcp-user.service';
import { checkWageRateType } from '../utils/checkWageRateType';
import { ClassificationRatesService } from '../../services/classification-rates.service';
import { map } from 'rxjs';
import {
  RateTemplateType,
  TrainingType,
  WageRate,
} from 'src/app/core/services/wage/wage.types';
import { TrainingTypeOptions } from '../enums/training-types-options.enum';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class MasterWageRateDataService {
  private readonly _masterWageRatesFormService = inject(
    MasterWageRateFormService
  );
  private readonly _classificationRatesService = inject(
    ClassificationRatesService
  );
  private readonly _lcpUserService = inject(LCPUserService);
  masterWageForm = this._masterWageRatesFormService.masterWageRatesForm;
  tenantId = toSignal(this._lcpUserService.getUserTenantId()) as Signal<string>;
  classificationRates = this._masterWageRatesFormService.classificationRateData;
  isAddMasterWageMode = this._masterWageRatesFormService.isAddMasterWageMode;
  isUpdateMasterWageMode =
    this._masterWageRatesFormService.isUpdateMasterWageMode;
  isCopyClassificationMode =
    this._masterWageRatesFormService.isCopyClassificationMode;
  isAddClassificationMode =
    this._masterWageRatesFormService.isAddClassificationMode;
  isCreatingNewMaster = computed(
    () =>
      this.isAddMasterWageMode() ||
      this.isAddClassificationMode() ||
      this.isCopyClassificationMode()
  );

  masterWageRateFactory() {
    if (this.isCreatingNewMaster()) {
      const createMasterWageVars = this.buildCreateMasterWageRateVars();
      return this._classificationRatesService
        .createMasterWage(createMasterWageVars)
        .pipe(map(data => data.createMasterWage));
    }
    const updateMasterWageRateVars = this.buildUpdateMasterWageRateVars();
    return this._classificationRatesService
      .updateMasterWage(updateMasterWageRateVars)
      .pipe(map(data => data.updateClassificationRate));
  }

  private buildCreateMasterWageRateVars(): CreateMasterWageRateVars {
    const { wageDeterminationInfo, craftClassifications } =
      this.masterWageForm.getRawValue();
    const trainingTypeValue =
      craftClassifications.trainingType !== TrainingTypeOptions.Other
        ? (craftClassifications.trainingType as TrainingType)
        : null;
    return {
      tenantId: this.tenantId(),
      input: {
        constructionTypeId: wageDeterminationInfo.constructionTypeId ?? '',
        trainingType: trainingTypeValue,
        craftClassificationApprenticeLevel: '',
        craftClassificationIsApprenticeableCraft: false,
        craftClassificationName:
          craftClassifications.craftClassificationName ?? '',
        craftName: craftClassifications.craftName ?? '',
        expirationDate: wageDeterminationInfo.expirationDate ?? '',
        issueDate: wageDeterminationInfo.issueDate ?? '',
        publicationDate: wageDeterminationInfo.publicationDate ?? '',
        jurisdictionLocationId:
          wageDeterminationInfo.jurisdictionLocationId ?? '',
        modificationName:
          wageDeterminationInfo.wageDeterminationModificationName ?? '',
        wageDeterminationName:
          wageDeterminationInfo.wageDeterminationName ?? '',
        wageRateShiftTypeId: craftClassifications.wageRateShiftTypeId ?? '',
        wageRate: this.buildWageRate(),
      },
    };
  }

  private buildUpdateMasterWageRateVars(): UpdateMasterWageRateVars {
    const { wageDeterminationInfo, craftClassifications } =
      this.masterWageForm.getRawValue();
    const trainingTypeValue =
      craftClassifications.trainingType !== TrainingTypeOptions.Other
        ? (craftClassifications.trainingType as TrainingType)
        : null;
    const { id: classificationRateId } = this.classificationRates();

    return {
      id: classificationRateId,
      tenantId: this.tenantId(),
      input: {
        craftClassificationName:
          craftClassifications.craftClassificationName ?? '',
        trainingType: trainingTypeValue,
        craftName: craftClassifications.craftName ?? '',
        expirationDate: wageDeterminationInfo.expirationDate ?? '',
        issueDate: wageDeterminationInfo.issueDate ?? '',
        publicationDate: wageDeterminationInfo.publicationDate ?? '',
        wageRate: this.buildWageRate(),
        wageRateShiftTypeId: craftClassifications.wageRateShiftTypeId ?? '',
      },
    };
  }

  private buildWageRate(): WageRate {
    const { wageRates } = this.masterWageForm.getRawValue();
    const wageRateTemplate = this._masterWageRatesFormService.wageRateTemplate;
    const isExtendedWages =
      wageRateTemplate() === RateTemplateType.ExtendedWageRates;

    return {
      baseFringeRate: checkWageRateType(wageRates.baseFringeRate ?? ''),
      baseWageRate: checkWageRateType(wageRates.baseWageRate ?? ''),
      standardHoursDay: checkWageRateType(wageRates.standardHoursDay ?? ''),
      totalDoubletimeRate: checkWageRateType(
        wageRates.totalDoubleTimeRate ?? ''
      ),
      totalHourlyRate: checkWageRateType(wageRates.totalHourlyRate ?? ''),
      totalOvertimeRate: checkWageRateType(wageRates.totalOvertimeRate ?? ''),
      trainingRate: checkWageRateType(wageRates.trainingRate ?? ''),
      ...(isExtendedWages && {
        extendedWageRate: {
          doubletimeBaseRate: checkWageRateType(
            wageRates.extendedWages?.doubletimeBaseRate ?? ''
          ),
          doubletimeFringeRate: checkWageRateType(
            wageRates.extendedWages?.doubletimeFringeRate ?? ''
          ),
          firstShiftBaseRate: checkWageRateType(
            wageRates.extendedWages?.firstShiftBaseRate ?? ''
          ),
          holidayFringeRate: checkWageRateType(
            wageRates.extendedWages?.holidayFringeRate ?? ''
          ),
          overtimeBaseRate: checkWageRateType(
            wageRates.extendedWages?.overtimeBaseRate ?? ''
          ),
          overtimeFringeRate: checkWageRateType(
            wageRates.extendedWages?.overtimeFringeRate ?? ''
          ),
          saturdayBaseRate: checkWageRateType(
            wageRates.extendedWages?.saturdayBaseRate ?? ''
          ),
          saturdayFringeRate: checkWageRateType(
            wageRates.extendedWages?.saturdayFringeRate ?? ''
          ),
          standardHoursWeek: checkWageRateType(
            wageRates.extendedWages?.standardHoursWeek ?? ''
          ),
          sundayBaseRate: checkWageRateType(
            wageRates.extendedWages?.sundayBaseRate ?? ''
          ),
          sundayFringeRate: checkWageRateType(
            wageRates.extendedWages?.sundayFringeRate ?? ''
          ),
          totalSaturdayRate: checkWageRateType(
            wageRates.extendedWages?.totalSaturdayRate ?? ''
          ),
          totalSundayRate: checkWageRateType(
            wageRates.extendedWages?.totalSundayRate ?? ''
          ),
        },
      }),
      notes: wageRates.notes ?? '',
      footNotes: [],
    };
  }
}
